import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { commonColumnDataDefinition } from "$root/ui-lib/table/column/common-data-definitions";
import { isRowBlocked } from "$root/ui-lib/table/custom-column/instrument/blocking-issue";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { multiRef } from "$root/utils";
import type { UserInstrumentDto, IndexTicker, ReviewTicker } from "@eu.mdotm.api/sphere";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { TextInput, type BulkActionItem, BulkActions } from "@mdotm/shadcn/mdotm/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { noop } from "@mdotm/shadcn/legacy/utils";
import type { ColDef } from "ag-grid-community";
import type { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { type Ref, useMemo, useState } from "react";
import { spawnInstrumentClassificationDialog } from "../instruments/instrument-classification/InstrumentClassificationDialog";
import type { ReviewGridRow, ReviewInstrument } from "./const";

export type EditableProxiedInstrument = Omit<ReviewTicker, "description" | "type"> & UserInstrumentDto;
export type ProxiedInstrumentData = {
	proxies: Array<IndexTicker>;
};

export type EditProxiedInstrumentTableProps<T extends ReviewGridRow = ReviewInstrument> = {
	mode?: "edit" | "readonly";
	rows: T[];
	columns: ColDef<T>[];
	onDelete(deleted: string[]): void;
	onRestore(restored: string[]): void;
	onProxy(proxies: Array<{ id: string; proxies: Array<IndexTicker> }>): void;
	onResolveIssue(proxies: Array<{ id: string } & Pick<T, "newAlias" | "proxies" | "hasBeenReviewed">>): void;
	onRowsValueChange(rows: T[]): void;
	agGridRef?: Ref<AgGridReact<T>>;
	/** Rows a grid keeps out of its checkbox selection on top of the blocked ones. */
	isRowSelectable?: (row: T) => boolean;
	/** Rows the search never filters out. */
	keepRow?: (row: T) => boolean;
} & Partial<Omit<AgGridReactProps<T>, "ref" | "isRowSelectable">>;

export function ReviewInstrumentsTable<T extends ReviewGridRow>({
	rows,
	columns,
	mode,
	onDelete,
	onProxy,
	onRestore,
	onRowsValueChange,
	onResolveIssue,
	agGridRef,
	isRowSelectable,
	keepRow,
	...agProps
}: EditProxiedInstrumentTableProps<T>): React.JSX.Element {
	const [selectionState, setSelection] = useState<string[]>([]);
	const [gridEl, setGridEl] = useState<AgGridReact<T> | null>(null);

	const instrumentsThatNeedProxy = useMemo(
		() => rows.filter((row) => row.needsCustomProxy && !row.delisted).map((row) => row.ticker),
		[rows],
	);

	const { filtered, query, setQuery } = useSearchable({
		collection: rows,
		extractFn: (row) => Object.values(row).join(" "),
	});
	const rowData = useMemo(
		() => (keepRow ? rows.filter((row) => keepRow(row) || filtered.includes(row)) : filtered),
		[rows, filtered, keepRow],
	);

	const actions = useMemo(
		() =>
			({
				editRiskModel: {
					label: "Edit",
					icon: "Edit",
					onClick: () => {
						spawnInstrumentClassificationDialog({
							onSubmitAsync: ({ indices, weights }) => {
								const proxies = indices.map(
									(props): IndexTicker => ({
										...props,
										weight: props.ticker ? weights.get(props.ticker)?.toNumber() : undefined,
									}),
								);
								const selection = gridEl?.api.getSelectedRows() ?? [];
								const rowsToUpdate = selection
									.map((row) => ({ ...row, proxies, hasBeenReviewed: true }))
									.filter((inst) => instrumentsThatNeedProxy.includes(inst.ticker));
								gridEl?.api.applyTransactionAsync({ update: rowsToUpdate });
								onRowsValueChange(rowsToUpdate);
							},
						}).catch(noop);
					},
				},
				delete: {
					label: "Delete",
					icon: "Delete",
					onClick: () => {
						const selection = gridEl?.api.getSelectedRows() ?? [];
						const rowsToUpdate = selection.map((row) => ({ ...row, deleted: true }));
						gridEl?.api.applyTransactionAsync({ update: rowsToUpdate });
						onRowsValueChange(rowsToUpdate);
					},
				},
				restore: {
					label: "Restore",
					icon: "restore",
					onClick: () => {
						const selection = gridEl?.api.getSelectedRows() ?? [];
						const rowsToUpdate = selection.map((row) => ({ ...row, deleted: false }));
						gridEl?.api.applyTransactionAsync({ update: rowsToUpdate });
						onRowsValueChange(rowsToUpdate);
					},
				},
			}) satisfies Record<string, BulkActionItem>,
		[gridEl?.api, instrumentsThatNeedProxy, onRowsValueChange],
	);

	return (
		<>
			<div className="pb-2">
				<div className="flex justify-between">
					<div className="grow max-w-[500px]">
						<TextInput
							leftContent={<Icon icon="Search" />}
							value={query}
							onChangeText={setQuery}
							placeholder="Filter by instrument name, ISIN, asset class or micro asset class"
						/>
					</div>
				</div>
			</div>
			<BulkActions
				variant="floating"
				onClear={() => gridEl?.api.deselectAll()}
				count={selectionState.length}
				data-qualifier="EditProxiedInstrument/Action/DeleteRestore"
				actions={mode === "readonly" ? [] : [actions.editRiskModel, actions.delete, actions.restore]}
			/>

			<div className="min-h-0 flex-1 basis-0!">
				<AgGridWithModules<T>
					{...agExtra.checkboxColumn({
						mode: "multiRow",
						hideDisabledCheckboxes: true,
						isRowSelectable(prams) {
							return prams.data == null || (!isRowBlocked(prams.data) && (isRowSelectable?.(prams.data) ?? true));
						},
					})}
					innerRef={multiRef(setGridEl, agGridRef)}
					columnDefs={columns}
					rowData={rowData}
					dataTypeDefinitions={commonColumnDataDefinition}
					onSelectionChanged={(params) => setSelection(() => params.api.getSelectedRows().map((x) => x.id))}
					getRowId={(row) => row.data.id}
					onCellValueChanged={(params) => {
						if (params.column.getColId() === "ACTION") {
							if (params.data.deleted) {
								onDelete([params.data.id]);
							} else {
								onRestore([params.data.id]);
							}
							return;
						}

						if (params.column.getColId() === "ISSUE") {
							onResolveIssue([
								{
									id: params.data.id,
									...params.newValue,
								},
							]);
						}
					}}
					rowClassRules={{
						[`bg-[var(--Table-highlightedRowBackgroundColor)]`]: (params) => {
							return Boolean(params.data?.deleted);
						},
						[`bg-[var(--global-palette-warning-100)]`]: (params) => {
							const needsReview =
								params.data?.issue === "MISSING_RISK_MODEL" ||
								params.data?.issue === "NOT_SUPPORTED_BOND" ||
								params.data?.issue === "RISK_MODEL_SUGGESTION_REQUIRES_REVIEW" ||
								params.data?.issue === "UNRECOGNIZED_INSTRUMENT" ||
								params.data?.issue === "MISSING_CLOSES";

							return Boolean(
								needsReview &&
									!params.data?.hasBeenReviewed &&
									!params.data?.deleted &&
									params.data &&
									!isRowBlocked(params.data),
							);
						},
					}}
					{...agProps}
				/>
			</div>
		</>
	);
}
